import {
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { camelCase, first, get, has, isObject, join } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type InputComponent } from '..';

export type InputSelectBarOption =
  | {
      value: string | number;
      label: string;
      order?: number;
      disabled?: boolean;
    }
  | string
  | number;

/** Combined label length beyond which the options stack on a phone. */
const STACK_ABOVE_LABEL_LENGTH = 30;

export interface InputSelectBarProps extends InputComponent {
  options?: InputSelectBarOption[];
}

export function InputSelectBar({
  label,
  nameOverride,
  hint,
  disabled,
  required,
  options = [],
}: InputSelectBarProps) {
  const { setFieldValue, values, errors } =
    useFormikContext<Record<string, string>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const selectedOption = useMemo(() => {
    if (has(values, name)) {
      return get(values, name);
    }
    return first(options);
  }, [name, options, values]);

  const handleSelect = useCallback(
    (option: InputSelectBarOption) =>
      setFieldValue(name, isObject(option) ? option.value : option),
    [name, setFieldValue],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  // Side by side, long labels wrap to two lines each at different widths on a
  // phone and the bar reads as a jumble. Past a certain amount of text the
  // options stack instead, one full-width button per line. Short bars
  // (Yes / No) stay side by side.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const labelLength = options.reduce<number>(
    (total, option) =>
      total + String(isObject(option) ? option.label : option).length,
    0,
  );
  const stacked = isPhone && labelLength > STACK_ABOVE_LABEL_LENGTH;

  return (
    <FormControl fullWidth sx={{ mt: 2 }}>
      {label && (
        <InputLabel shrink sx={{ top: -8, left: -8 }}>
          {label}
        </InputLabel>
      )}
      <ButtonGroup
        size={'small'}
        disableElevation
        orientation={stacked ? 'vertical' : 'horizontal'}
        fullWidth={stacked}
      >
        {options?.map((option, i) => (
          <Button
            disabled={disabled || (isObject(option) && option.disabled)}
            sx={{ textTransform: 'none', px: 2 }}
            key={i}
            variant={
              (isObject(option) ? option.value : option) === selectedOption
                ? 'contained'
                : 'outlined'
            }
            onClick={() => handleSelect(option)}
          >
            {isObject(option) ? option.label : option}
          </Button>
        ))}
      </ButtonGroup>
      {(!!error || !!hint) && (
        <FormHelperText error={!!error}>{error || hint}</FormHelperText>
      )}
    </FormControl>
  );
}
